import { linkElement } from './elements/link.element';
import externalLinkIcon from '../assets/external-link.svg';
import { BrowserObjectModel } from '../components/browser-object-model';
import { Injectable } from '../components/dependency-injection/decorators';
import { StylesheetRenderer } from '../components/renderer/stylesheet.renderer';
import { CreativeTypeEnum, LinkingTypeEnum } from '../dto/recs.dto';
import { AdSlotContainerItem } from '../entities/ad-slot-container-item';
import { CreativeViewInterface } from '../interfaces/containers/creative-view.interface';
import { HtmlElementContext } from '../types';
import { subtitleElement } from './elements/subtitle.element';

@Injectable()
export class LinkCreativeView implements CreativeViewInterface {
  public constructor (
    private readonly browserObjectModel: BrowserObjectModel,
    private readonly stylesheetRenderer: StylesheetRenderer,
  ) {
  }

  public render (item: AdSlotContainerItem): Element {
    const adSlot = item.getAdSlotContainer()?.getAdSlot();

    if (!adSlot) {
      throw new Error('AdSlot is required to render LinkCreativeView');
    }

    const itemEl = this.browserObjectModel.getDocument().createElement('null');
    const linkProperties = adSlot.getSettings().getLinkProperties();
    const linkingType = item.getLinkingType();
    const htmlElementContext: HtmlElementContext = {
      adSlot,
      dom: this.browserObjectModel.getDocument(),
      stylesheet: this.stylesheetRenderer.getStylesheet(adSlot),
    };

    const linkEl = linkElement({
      href: item.getUrl() ?? '',
      className: this.stylesheetRenderer.getClass(adSlot, 'listItemLink'),
      linkingType,
      textContent: item.getTitle() ?? '',
      attributes: {
        'data-id': item.getId(),
        'data-c-id': item.getCampaignId(),
        'data-p-id': item.getPublicationId(),
        'data-linking-type': linkingType?.toString(),
      },
      ...linkProperties[linkingType === LinkingTypeEnum.INTERNAL ? 'internal' : 'external'],
    }, htmlElementContext);

    // linkEl.appendChild(
    //   titleElement({ textContent: item.getTitle() }, htmlElementContext),
    // );

    if (linkingType === LinkingTypeEnum.EXTERNAL) {
      const externalLinkIconElement = this.browserObjectModel.getDocument().createElement('span');
      externalLinkIconElement.className = this.stylesheetRenderer.getClass(adSlot, 'listItemTitleExternalLinkIcon');
      externalLinkIconElement.innerHTML = externalLinkIcon;
      linkEl.appendChild(externalLinkIconElement);
    }

    const metaEl = this.browserObjectModel.getDocument().createElement('div');
    metaEl.className = this.stylesheetRenderer.getClass(adSlot, 'listItemMeta');

    const subtitleEl = subtitleElement({ subtitle: item.getSubtitle() }, htmlElementContext);

    if (subtitleEl) {
      metaEl.appendChild(subtitleEl);
    } else {
      // @TODO option to choose from settings
      const publicationField = item.getPublicationAbbreviatedName()
      && (item.getPublicationAbbreviatedName()?.length ?? 0) > 0
        ? 'publicationAbbreviatedName'
        : 'publicationName';

      const sortedMetaElements = [ 'authors', publicationField, 'publicationDate' ];
      const metaElements: Element[] = [];

      for (const metaElementName of sortedMetaElements) {
        const camelCaseElementName = `${metaElementName.charAt(0).toUpperCase()}${metaElementName.slice(1)}`;
        const methodName = `get${camelCaseElementName}`;

        const metaElementValue = item[ methodName ] ? item[ methodName ]() : '';

        if (metaElementValue && (metaElementValue.length ?? 0) > 0) {
          const metaElementElement = this.browserObjectModel.getDocument().createElement('span');
          metaElementElement.className = this.stylesheetRenderer.getClass(
            adSlot,
            `listItem${camelCaseElementName}`,
          );
          metaElementElement.textContent = metaElementValue;
          metaElements.push(metaElementElement);
        }
      }

      for (let i = 0; i < metaElements.length; i++) {
        const metaElementElement = metaElements[ i ];
        const isLast = i === metaElements.length - 1;

        metaEl.appendChild(metaElementElement);

        if (!isLast) {
          metaEl.appendChild(this.browserObjectModel.getDocument().createTextNode(', '));
        }
      }
    }

    itemEl.appendChild(linkEl);
    itemEl.appendChild(metaEl);

    return itemEl;
  }

  public supports (item: AdSlotContainerItem): boolean {
    return item.getCreativeType() === CreativeTypeEnum.LINK;
  }
}
